import type { ApolloCache } from "@apollo/client"
import type { IconProps } from "@/v2-ui/icon"
import type { BadgeProps } from "@/v2-backoffice-client/components/badge"
import type { TableStatusFilterOptionProps } from "@/v2-backoffice-client/components/table/TableStatusFilter"
import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"
import {
  LICENSE_LIVE_STATUS_CODES,
  LICENSE_SOURCE_OPERATOR,
  LICENSE_STATUS_ACTIVE,
  LICENSE_STATUS_EXPIRED,
  LICENSE_STATUS_NONE,
  LICENSE_STATUS_NOT_RENEWING,
  LICENSE_STATUS_NOT_STARTING,
  LICENSE_STATUS_SCHEDULED,
  LICENSE_STATUS_TRIAL,
} from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

/**
 * Grouping/export label for a governing license status as the board procs
 * emit it: 1-6 named by the getLicenseStatuses lookup, 0 = "No license",
 * null/undefined = license tracking doesn't apply to the row.
 */
export function getLicenseGoverningStatusLabel(
  code: number | null | undefined,
  namesByCode: Map<number, string>,
): string {
  if (code == null) return ""
  if (code === LICENSE_STATUS_NONE) return "No license"
  return namesByCode.get(code) ?? String(code)
}

export type LicenseStatusVisual = {
  variant: BadgeProps["variant"]
  iconSrc: IconProps["src"]
}

/**
 * THE look of a license status: hue plus shape. The hue carries urgency the
 * way every backoffice badge does (green good, amber attention, red act-now,
 * grey no-signal), but several statuses share a hue — two reds, two ambers,
 * several greys — so a list of pills reads as look-alikes. The icon is what
 * tells them apart at a glance, and it is the same icon wherever the status
 * appears: boards, panels, user rows, history, the legend. An overdue trial
 * escalates to the red warning triangle; the resting trial is the hourglass.
 */
export function getLicenseStatusVisual(
  licenseStatusCode: number,
  options: { isTrialOverdue?: boolean } = {},
): LicenseStatusVisual {
  switch (licenseStatusCode) {
    case LICENSE_STATUS_TRIAL:
      return options.isTrialOverdue
        ? { variant: "critical", iconSrc: "triangle-warning" }
        : { variant: "warning", iconSrc: "hourglass" }
    case LICENSE_STATUS_ACTIVE:
      return { variant: "positive", iconSrc: "check" }
    case LICENSE_STATUS_SCHEDULED:
      return { variant: "neutral", iconSrc: "calendar-clock" }
    case LICENSE_STATUS_NOT_RENEWING:
      return { variant: "warning", iconSrc: "calendar-xmark" }
    case LICENSE_STATUS_EXPIRED:
      return { variant: "critical", iconSrc: "circle-xmark" }
    case LICENSE_STATUS_NOT_STARTING:
      return { variant: "outline", iconSrc: "ban" }
    default:
      return { variant: "neutral", iconSrc: "circle-dashed" }
  }
}

export type LicenseStatusFilterVisual = {
  variant: TableStatusFilterOptionProps["variant"]
  iconSrc: IconProps["src"]
}

/**
 * The same status look, translated for a board's status-filter tab, whose
 * icon colours come from a smaller palette (success, warning, destructive,
 * or plain). Keeps the tab a customer is filtered by and the pill on her
 * row recognisably the same thing.
 */
export function getLicenseStatusFilterVisual(
  licenseStatusCode: number,
): LicenseStatusFilterVisual {
  const visual = getLicenseStatusVisual(licenseStatusCode)
  const variant =
    visual.variant === "positive"
      ? "success"
      : visual.variant === "warning"
        ? "warning"
        : visual.variant === "critical"
          ? "destructive"
          : undefined
  return { variant, iconSrc: visual.iconSrc }
}

export type TrialFacts = {
  /** Calendar days until the trial ends; negative = overdue. */
  daysLeft: number
  isOverdue: boolean
  /** "5 days left" / "3 days overdue" / "ends today". */
  label: string
}

/**
 * The trial countdown, derived at render time from TrialEndsAt so a row can
 * never go stale between fetch and view. Only meaningful while the status is
 * still Trial: after a conversion the date remains as history, and callers
 * must not show a countdown for it. The Pick keeps it usable for the user
 * row's customer-license fields, not just full license rows.
 */
export function getTrialFacts(
  license: Pick<LicenseFragment, "LicenseStatusCode" | "TrialEndsAt">,
): TrialFacts | null {
  if (license.LicenseStatusCode !== LICENSE_STATUS_TRIAL) return null
  if (!license.TrialEndsAt) return null

  const daysLeft = getDaysUntil(license.TrialEndsAt)
  const isOverdue = daysLeft < 0

  let label: string
  if (daysLeft === 0) label = "ends today"
  else if (isOverdue)
    label = `${-daysLeft} ${-daysLeft === 1 ? "day" : "days"} overdue`
  else label = `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`

  return { daysLeft, isOverdue, label }
}

/**
 * Calendar days from today until a Datetime string, in the operator's local
 * timezone. Parses the date part only: the API returns DATE columns as UTC
 * midnight, which the Date constructor would shift across a day boundary in
 * negative-offset timezones.
 */
export function getDaysUntil(date: string): number {
  const [year, month, day] = date.slice(0, 10).split("-").map(Number)
  const end = new Date(year, month - 1, day)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((end.getTime() - today.getTime()) / ONE_DAY_IN_MS)
}

/**
 * Calendar days between two Datetime strings (date parts only, same parsing
 * contract as getDaysUntil) — the "14-day trial" duration in the badge
 * tooltip.
 */
export function getDaysBetween(from: string, to: string): number {
  const [fromYear, fromMonth, fromDay] = from
    .slice(0, 10)
    .split("-")
    .map(Number)
  const [toYear, toMonth, toDay] = to.slice(0, 10).split("-").map(Number)
  const start = new Date(fromYear, fromMonth - 1, fromDay)
  const end = new Date(toYear, toMonth - 1, toDay)
  return Math.round((end.getTime() - start.getTime()) / ONE_DAY_IN_MS)
}

/**
 * A yyyy-MM-dd value from an `<input type="date">` as the RFC3339 string the
 * Datetime scalar unmarshals (dates travel as UTC midnight, matching how the
 * API returns DATE columns).
 */
export function dateInputToDatetime(value: string): string {
  return `${value}T00:00:00Z`
}

/** Today as a yyyy-MM-dd `<input type="date">` value (local time). */
export function getDateInputToday(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate()).padStart(2, "0")
  return `${now.getFullYear()}-${month}-${day}`
}

/** Whether the backoffice may edit/delete this license (operator-sourced). */
export function isLicenseEditable(license: LicenseFragment): boolean {
  return license.LicenseSourceCode === LICENSE_SOURCE_OPERATOR
}

/**
 * Whether the license grants anything right now: a status that holds seats
 * (Trial, Active, Not renewing), the set acc.CustomerSeatUsage counts. The
 * trial clock is not read here; an ended trial flips to Expired.
 */
export function isLicenseLive(
  license: Pick<LicenseFragment, "LicenseStatusCode">,
) {
  return LICENSE_LIVE_STATUS_CODES.includes(license.LicenseStatusCode)
}

/**
 * Evicts every license read after a mutation. The list is fetched with
 * different variable sets (board: none, trials tab: status, panel: customer),
 * and evicting the field covers them all — same strategy as the customer
 * mutations.
 */
export function evictLicenseQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicenses" })
  // A grant can name seat holders; an end or delete takes theirs away.
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicenseSeatHolders" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicensePlanSeatHolders" })
  cache.gc()
}

/**
 * Evicts what a seat grant/revoke is visible through: the seat panel's own
 * read, the license rows, whose SeatsAssigned the badge holds against the
 * cap, and the plan side (the panel's Named Seats tab and the board's
 * NamedSeats count).
 */
export function evictLicenseSeatQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicenseSeatUsers" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicenseSeatHolders" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicensePlanSeatHolders" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicensePlans" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicenses" })
  cache.gc()
}

/**
 * Evicts the add-on reads after an add-on is added to or removed from a
 * customer: the customer-scoped reads (the Add-ons section, the Add-ons
 * column and header chips, the add dialog's IsHeld), the add-on panel's
 * Customers tab, the catalog whose GrantCount both the board and the panel's
 * Customers badge render, and the customer's Feature access (an add-on with
 * a live license changes what resolves).
 */
export function evictLicenseAddOnQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomerAddOns" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAddOns" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAddOnGrants" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAddOnCatalog" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomerFeatureAccess" })
  cache.gc()
}

/**
 * Evicts everything a feature-group edit can be visible through: the group
 * board itself, group membership reads, the license board's package count, every
 * license panel's Packages tab, both add-on reads (their package-name chips),
 * and the registry browser's rows (their "Bundled in" context names the
 * groups). All of these are small catalog queries — refetching them beats
 * reasoning about which of them the edited group is actually carried by.
 */
export function evictFeatureGroupQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroups" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroupFeatures" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroupRules" })
  // "Also in Basic, Standard" on the capabilities list reads membership.
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureSurfaceCatalog" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureContentCatalog" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicensePlans" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicensePlanFeatureGroups" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAddOnCatalog" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAddOns" })
  // The Changes feed shows each surface's current packages and availability.
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureSurfaceChanges" })
  cache.gc()
}

/**
 * Evicts what a "who should have it" decision on the Feature access board
 * changes: the board itself (availability, decision, carriers), the
 * capability catalog (availability and "carried by" on the row), the
 * package board's counts and a package's members list - and nothing the
 * decision cannot have touched.
 */
export function evictFeatureSurfaceDecisionQueries(
  cache: ApolloCache<unknown>,
) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureSurfaceChanges" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureSurfaceCatalog" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroups" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroupFeatures" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeature" })
  cache.gc()
}

/**
 * The same for a decision on a map layer or base map (Feature access > Content):
 * the content catalog (the board and the package editor's Content view read
 * it), the package board's counts and members, the feature row the panels
 * read, and the layer sheet's visibility gates.
 */
export function evictFeatureContentDecisionQueries(
  cache: ApolloCache<unknown>,
) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureContentCatalog" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroups" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroupFeatures" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeature" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLayerVisibility" })
  cache.gc()
}

/**
 * Evicts every surface an add-on edit shows up on: the catalog board, the
 * customer-scoped reads (the add dialog and the Add-ons section render name,
 * products, scope and package chips from them; a changed "goes with" moves
 * which license carries it), the panel's Customers tab (carrier per
 * customer), and the group board whose AddOnNames names the add-ons carrying
 * each package.
 */
export function evictAddOnCatalogQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAddOnCatalog" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAddOns" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomerAddOns" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getAddOnGrants" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroups" })
  cache.gc()
}

/**
 * Evicts the reads that render a license's grant mapping after re-declaring it:
 * the license panel's Features tab, the license board (FeatureCount badge), the
 * group board (PlanCount) and every group panel's Licenses tab.
 */
export function evictPlanFeatureGroupQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicensePlanFeatureGroups" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getLicensePlans" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroups" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getFeatureGroupPlans" })
  cache.gc()
}

/**
 * Evicts the migration worklist after a row write. The license list is NOT
 * evicted: a migration edit changes no license fact (LiveLicenseCount only moves
 * when the license itself moves, via the Fenerum sync).
 */
export function evictPlanMigrationQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getPlanMigrations" })
  cache.gc()
}

/**
 * Evicts the collections worklist after a follow-up write. The invoice reads
 * are NOT evicted: a follow-up edit changes no invoice fact — money truth
 * only moves via the Fenerum mirror.
 */
export function evictInvoiceCollectionQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getInvoiceCollections" })
  cache.gc()
}

/**
 * Evicts everything a billing-account link or review moves: the board (and
 * the panel fed from its cache entry), the panel's candidates card, and the
 * customer-side reads a link re-routes — the Billing tab's account row,
 * invoices and revenue stats all resolve through the link.
 */
export function evictBillingAccountQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getBillingAccounts" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getBillingAccountMatches" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomerBillingAccount" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomerInvoices" })
  cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomerRevenueStats" })
  cache.gc()
}

/**
 * What a license's seat badge holds against its cap: for a seated product
 * (the MCP servers; lic.Products.SeatsAreAssigned) the people named on the
 * license, for every other product the customer's active users plus open
 * invitations — the same number the customer header shows.
 */
export function occupiedSeatsOf(license: {
  SeatsAreAssigned: boolean
  SeatsAssigned: number
  UserCount: number
  PendingInvitationCount: number
}): number {
  return license.SeatsAreAssigned
    ? license.SeatsAssigned
    : license.UserCount + license.PendingInvitationCount
}
